/**
 * `/locations/united-states/<stateUrlLabel>/<locationSlug>/`, for example
 * `/locations/united-states/illinois/chicago/`: one US location's page (details, hours, map, local
 * solutions, management team, and, when enabled, the location contact form), rendered by
 * `components/Locations/IndividualLocationContainer.tsx`.
 *
 * Which pages exist: for each US state in the header location data, the slugs of the brand
 * locations whose Contentful `state` equals the state's English `label`
 * (`src/contentful/location/getContentfulLocationSlugsByStateOrProvince.ts`). A location whose
 * `state` text matches no header label (a misspelling or an abbreviation such as "IL") gets no
 * page and no build error.
 *
 * `getStaticProps` looks the location up by slug alone
 * (`src/contentful/location/getContentfulLocationDetailsByLocationSlug.ts`), not by state, so two
 * locations sharing a slug would both get the first match's content.
 *
 * No `NEXT_PUBLIC_COMPANY` check, unlike the Canada and Mexico location pages, so the dormant
 * Southern Tool Steel build builds it too. The route tree is in `src/pages/locations/index.tsx`.
 * Pinned by `src/unit-test/pages/locations/united-states/stateUrlLabel/locationSlug.spec.tsx`.
 */
import IndividualLocationContainer from "@components/Locations/IndividualLocationContainer";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { getContentfulLocationDetailsByLocationSlug } from "@contentful/location/getContentfulLocationDetailsByLocationSlug";
import { getContentfulLocationSlugsByStateOrProvince } from "@contentful/location/getContentfulLocationSlugsByStateOrProvince";
import { ProcessedContentfulLocationAllFieldsByLanguage } from "@contentful/location/types";
import { Language } from "@ryerson/frontend.application";
import { Country } from "@ryerson/frontend.common";
import {
	allContentfulHeaderCalls,
	getFlattenedLocationsHeaderData,
	HeaderPageProps,
} from "@ryerson/frontend.contentful";
import { GetStaticPathsResult, GetStaticProps } from "next";
import React from "react";

/** One path per (header state `urlLabel`, location slug) pair; see the file header. */
export const getStaticPaths = async () => {
	const {
		countries: { [Country.us]: usStates },
	} = await getFlattenedLocationsHeaderData();

	const paths: GetStaticPathsResult["paths"] = [];

	// forEach will not wait for the async function to finish
	for (const stateObj of usStates) {
		const locationSlugsByState = await getContentfulLocationSlugsByStateOrProvince(
			stateObj.label[Language.en]
		);

		for (const locationSlug of locationSlugsByState) {
			paths.push({
				params: {
					stateUrlLabel: stateObj.urlLabel,
					locationSlug: locationSlug,
				},
			});
		}
	}

	return {
		paths: paths,
		fallback: false, // returns 404 for all other paths
	};
};

type PageParams = {
	stateUrlLabel: string;
	locationSlug: string;
};

type PageProps = {
	contentfulLocationByLanguage: ProcessedContentfulLocationAllFieldsByLanguage;
};

/**
 * Build time: the header data and the location in all three languages. The lookup throws when the
 * slug has no entry in one of the languages, which fails the build.
 */
export const getStaticProps: GetStaticProps<PageProps, PageParams> = async ({ params }) => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	const contentfulLocationByLanguage = await getContentfulLocationDetailsByLocationSlug(
		params?.locationSlug as string
	); // casting as this param will always be defined

	return {
		props: {
			...headerFooterProps,
			contentfulLocationByLanguage,
		},
	};
};

/** A US location's page; see the file header. */
const Page: React.FC<HeaderPageProps & PageProps> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<IndividualLocationContainer
				contentfulLocationByLanguage={props.contentfulLocationByLanguage}
			/>
		</HeaderLayout>
	);
};

export default Page;
